<template>
  <div>
    <!-- Loading Overlay -->
    <b-overlay :show="isLoading" spinner-variant="primary" rounded="sm">
      <!-- Form -->
      <div class="mt-2">
        <div v-if="isLoading">
          <b-skeleton :loading="isLoading" height="100px" />

          <h1>Loading Item...</h1>
        </div>
        <items-form v-else :form-data.sync="formData" :loading="isSubmitting" @submit="onSubmit" />
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BButton, BOverlay, BSkeleton } from 'bootstrap-vue';
import { ref, onMounted, watch } from '@vue/composition-api';
import store from '@/store';
import { api } from '@/utils/api';
import ItemsForm from './ItemsForm.vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import { useToast } from 'vue-toastification/composition';

export default {
  name: 'ItemsEdit',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BSkeleton,
    ItemsForm,
    ToastificationContent,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props, { root }) {
    const toast = useToast();

    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const formData = ref({
      id: 0,
      itemCode: '',
      itemName: '',
      unitID: null,
      itemGroupID: null,
      itemHSCodeID: null,
      itemPurchaseRate: 0,
      tradePrice: 0,
      retailPrice: 0,
      salesTaxPercent: 0,
      addSalesTaxPercent: 0,
      isActive: true,
      addedBy: 0,
      addedOn: '',
      updatedBy: 0,
      updatedOn: '',
      metaAddedBy: '',
      metaUpdatedBy: '',
      meta_unit: '',
      meta_itemGroup: '',
      meta_HSCode: '',
      businessID: store.state.user.businessID,
      isDeleted: false,
      deletedByUserID: 0,
    });

    const fetchItemData = async () => {
      if (!props.id) return;

      try {
        // Direct API call to LoadSelectedData
        const response = await api.get(
          `/ItemsFBR/LoadSelectedData/${store.state.user.businessID}?id=${props.id}&userID=${store.state.user.userID}`
        );

        // Handle different possible response structures
        let fetchedItem = null;

        if (response.data && response.data.data && response.data.data.itemsFBR) {
          fetchedItem = response.data.data.itemsFBR;
        } else if (response.data && response.data.itemsFBR) {
          fetchedItem = response.data.itemsFBR;
        } else if (response.data && response.data.data && response.data.data.entity) {
          fetchedItem = response.data.data.entity;
        } else if (response.data && response.data.entity) {
          fetchedItem = response.data.entity;
        } else if (response.data && response.data.id) {
          fetchedItem = response.data;
        } else {
          console.error('Unexpected response structure:', response);
          console.error('Response type:', typeof response);
          console.error('Response keys:', response ? Object.keys(response) : 'null/undefined');
          throw new Error('Invalid response structure from API');
        }

        // Map API fields to form fields
        formData.value = {
          id: fetchedItem.id || 0,
          itemCode: fetchedItem.itemCode || '',
          itemName: fetchedItem.itemName || '',
          unitID: fetchedItem.unitID || null,
          itemGroupID: fetchedItem.itemGroupID || null,
          itemHSCodeID: fetchedItem.itemHSCodeID || null,
          itemPurchaseRate: fetchedItem.itemPurchaseRate || 0,
          tradePrice: fetchedItem.tradePrice || 0,
          retailPrice: fetchedItem.retailPrice || 0,
          salesTaxPercent: fetchedItem.salesTaxPercent || 0,
          addSalesTaxPercent: fetchedItem.addSalesTaxPercent || 0,
          isActive: fetchedItem.isActive !== undefined ? fetchedItem.isActive : true,
          addedBy: fetchedItem.addedBy || 0,
          addedOn: fetchedItem.addedOn || '',
          updatedBy: fetchedItem.updatedBy || 0,
          updatedOn: fetchedItem.updatedOn || '',
          metaAddedBy: fetchedItem.metaAddedBy || '',
          metaUpdatedBy: fetchedItem.metaUpdatedBy || '',
          meta_unit: fetchedItem.meta_unit || '',
          meta_itemGroup: fetchedItem.meta_itemGroup || '',
          meta_HSCode: fetchedItem.meta_HSCode || '',
          businessID: store.state.user.businessID,
          isDeleted: fetchedItem.isDeleted || false,
          deletedByUserID: fetchedItem.deletedByUserID || 0,
        };
      } catch (error) {
        console.error('Error fetching item:', error);
        store.dispatch('app/showToast', {
          title: 'Error',
          text: 'Failed to load item data',
          variant: 'danger',
        });
      } finally {
        isLoading.value = false;
      }
    };

    const onSubmit = async (submittedFormData) => {
      isSubmitting.value = true;

      try {
        // Use the submitted form data from the child component
        const dataToSubmit = submittedFormData || formData.value;
        dataToSubmit.businessID = store.state.user.businessID;

        // Prepare payload with itemsFBR wrapper and userID
        const payload = {
          itemsFBR: {
            id: dataToSubmit.id,
            itemCode: dataToSubmit.itemCode,
            itemName: dataToSubmit.itemName,
            unitID: dataToSubmit.unitID,
            itemGroupID: dataToSubmit.itemGroupID,
            itemPurchaseRate: dataToSubmit.itemPurchaseRate,
            tradePrice: dataToSubmit.tradePrice,
            retailPrice: dataToSubmit.retailPrice,
            salesTaxPercent: dataToSubmit.salesTaxPercent,
            addSalesTaxPercent: dataToSubmit.addSalesTaxPercent,
            itemHSCodeID: dataToSubmit.itemHSCodeID,
            isActive: dataToSubmit.isActive,
            businessID: store.state.user.businessID,
          },
          userID: store.state.user.userID,
        };

        // Direct API call
        const response = await api.post('/ItemsFBR/Save', payload);

        console.log('=== ItemsEdit API Response:', response);

        // Show success toast
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Item added successfully',
            variant: 'success',
            icon: 'CheckIcon',
          },
        });

        // Navigate back to items list using root.$router
        // root.$router.push('/apps/fbr-invoice/items');
      } catch (error) {
        console.error('Error updating item:', error);

        // Show user-friendly error message
        let errorMessage = 'Error updating item. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: errorMessage,
            variant: 'danger',
            icon: 'XIcon',
          },
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    // Debug logging for formData changes (commented out to prevent infinite loop)
    watch(
      formData,
      (newVal, oldVal) => {
        console.log('=== ItemsEdit formData changed:', JSON.stringify(newVal, null, 2));
      },
      { deep: true }
    );

    // Fetch item data when component mounts
    onMounted(() => {
      fetchItemData();
    });

    return {
      formData,
      isLoading,
      isSubmitting,
      onSubmit,
    };
  },
};
</script>

<style lang="scss" scoped>
// Add any custom styles here
</style>
